import {
    AlertCircle,
    CheckCircle2,
    Headphones,
    Loader2,
    LockKeyhole,
    Mail,
    Plus,
    RefreshCw,
    Search,
    ShieldCheck,
    UserRound,
    X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { fetchApp } from "../../services/apiService";

type CustomerCareUser = {
  id: string;
  client_id: string;
  username: string;
  email: string;
  fcm_token?: string | null;
  role: string;
  created_at?: string | null;
  updated_at?: string | null;
};

type SupportUsersResponse = {
  items: CustomerCareUser[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
};

export default function CustomerCareUsersPage() {
  const [users, setUsers] = useState<CustomerCareUser[]>([]);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [resetUser, setResetUser] = useState<CustomerCareUser | null>(null);

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [newPassword, setNewPassword] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadUsers = async (pageToLoad = page) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set("page", String(pageToLoad));
      params.set("page_size", String(pageSize));

      if (search.trim()) {
        params.set("q", search.trim());
      }

      const res = await fetchApp<SupportUsersResponse>(
        `/customer-care-auth/support-users?${params.toString()}`,
      );

      setUsers(res.items || []);
      setPage(res.page || pageToLoad);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (err: any) {
      setError(err?.message || "Failed to load customer care users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateModal = () => {
    setForm({
      username: "",
      email: "",
      password: "",
    });
    setShowCreateModal(true);
    setError(null);
    setSuccess(null);
  };

  const closeCreateModal = () => {
    if (saving) return;

    setShowCreateModal(false);
    setForm({
      username: "",
      email: "",
      password: "",
    });
  };

  const createSupportUser = async () => {
    if (!form.username.trim()) {
      setError("Username is required.");
      return;
    }

    if (!form.email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!form.password.trim()) {
      setError("Password is required.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await fetchApp("/customer-care-auth/support-users", {
        method: "POST",
        body: JSON.stringify({
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password.trim(),
          fcm_token: null,
        }),
      });

      setSuccess("Customer care user created successfully.");
      closeCreateModal();
      await loadUsers(1);
    } catch (err: any) {
      setError(err?.message || "Failed to create customer care user.");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!resetUser) return;

    if (!newPassword.trim()) {
      setError("New password is required.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await fetchApp(`/customer-care-auth/${resetUser.id}/reset-password`, {
        method: "POST",
        body: JSON.stringify({
          new_password: newPassword.trim(),
        }),
      });

      setSuccess(`Password reset for ${resetUser.username}.`);
      setResetUser(null);
      setNewPassword("");
    } catch (err: any) {
      setError(err?.message || "Failed to reset password.");
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadUsers(1);
  };

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-emerald-700">
                <Headphones className="h-3.5 w-3.5" />
                Support Management
              </div>

              <h1 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                Customer Care Users
              </h1>

              <p className="mt-1 max-w-2xl text-sm font-medium text-slate-500">
                Create and manage support users who can access the customer care
                dashboard, search customers, create tickets, and notify admins.
              </p>
            </div>

            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 text-sm font-black text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-600"
            >
              <Plus className="h-4 w-4" />
              Add Support User
            </button>
          </div>
        </section>

        {error && (
          <AlertBox
            type="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {success && (
          <AlertBox
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
          />
        )}

        <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard
            label="Total Support Users"
            value={total}
            icon={<UserRound className="h-5 w-5" />}
          />

          <StatCard
            label="Current Page"
            value={users.length}
            icon={<Headphones className="h-5 w-5" />}
          />

          <StatCard
            label="Role"
            value="Customer Care"
            icon={<ShieldCheck className="h-5 w-5" />}
          />
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-black text-slate-950">Support Users List</h2>
              <p className="text-sm font-medium text-slate-500">
                Showing {users.length} of {total} support users.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  placeholder="Search username or email"
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 pl-10 pr-4 text-sm font-semibold outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 sm:w-80"
                />
              </div>

              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Search
              </button>

              <button
                type="button"
                onClick={() => loadUsers(page)}
                disabled={loading}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex min-h-[300px] items-center justify-center">
              <div className="text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-emerald-500" />
                <p className="mt-3 text-sm font-bold text-slate-500">
                  Loading support users...
                </p>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex min-h-[300px] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50">
              <div className="text-center">
                <UserRound className="mx-auto h-10 w-10 text-slate-400" />
                <p className="mt-3 font-black text-slate-800">
                  No support users found
                </p>
                <p className="mt-1 text-sm font-medium text-slate-500">
                  Create a customer care user to start.
                </p>

                <button
                  type="button"
                  onClick={openCreateModal}
                  className="mt-4 inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-4 text-sm font-black text-white transition hover:bg-emerald-600"
                >
                  <Plus className="h-4 w-4" />
                  Add Support User
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-emerald-200 hover:bg-emerald-50/40"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-lg font-black uppercase text-emerald-700">
                        {user.username?.charAt(0) || "S"}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="truncate font-black text-slate-950">
                          {user.username}
                        </p>

                        <p className="mt-1 flex items-center gap-1.5 truncate text-sm font-semibold text-slate-500">
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          {user.email}
                        </p>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                            Customer Care
                          </span>

                          {user.fcm_token ? (
                            <span className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-black text-sky-700">
                              FCM Ready
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-500">
                              No FCM Token
                            </span>
                          )}
                        </div>

                        <p className="mt-3 text-xs font-medium text-slate-400">
                          Created {formatDate(user.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setResetUser(user);
                          setNewPassword("");
                          setError(null);
                          setSuccess(null);
                        }}
                        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 text-sm font-black text-amber-700 transition hover:bg-amber-100"
                      >
                        <LockKeyhole className="h-4 w-4" />
                        Reset Password
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold text-slate-500">
                  Page <span className="font-black text-slate-800">{page}</span>{" "}
                  of{" "}
                  <span className="font-black text-slate-800">
                    {totalPages}
                  </span>
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => loadUsers(page - 1)}
                    className="h-10 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Previous
                  </button>

                  <button
                    type="button"
                    disabled={page >= totalPages || loading}
                    onClick={() => loadUsers(page + 1)}
                    className="h-10 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4">
              <div>
                <h3 className="font-black text-slate-950">Add Support User</h3>

                <p className="mt-1 text-sm font-medium text-slate-500">
                  Create login access for a customer care staff member.
                </p>
              </div>

              <button
                type="button"
                onClick={closeCreateModal}
                disabled={saving}
                className="rounded-2xl p-2 text-slate-500 transition hover:bg-slate-200 disabled:opacity-60"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">
                  Username
                </label>

                <input
                  value={form.username}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, username: e.target.value }))
                  }
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
                  placeholder="support.demo"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">
                  Email
                </label>

                <input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
                  placeholder="support@example.com"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">
                  Temporary Password
                </label>

                <input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, password: e.target.value }))
                  }
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
                  placeholder="Temporary password"
                />
              </div>

              <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-700">
                This user will belong to your client account and will be created
                with the role <b>customer_care</b>.
              </div>

              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={saving}
                  className="h-11 rounded-2xl border border-slate-300 bg-white px-5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={createSupportUser}
                  disabled={saving}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 text-sm font-black text-white transition hover:bg-emerald-600 disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-amber-50 px-5 py-4">
              <div>
                <h3 className="font-black text-slate-950">Reset Password</h3>

                <p className="mt-1 text-sm font-medium text-slate-600">
                  Set a new password for{" "}
                  <span className="font-black">{resetUser.username}</span>.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setResetUser(null)}
                disabled={saving}
                className="rounded-2xl p-2 text-slate-500 transition hover:bg-amber-100 disabled:opacity-60"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">
                  New Password
                </label>

                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10"
                  placeholder="Enter new password"
                />
              </div>

              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setResetUser(null)}
                  disabled={saving}
                  className="h-11 rounded-2xl border border-slate-300 bg-white px-5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={resetPassword}
                  disabled={saving}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-amber-500 px-5 text-sm font-black text-white transition hover:bg-amber-600 disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-2xl font-black tracking-tight text-slate-950">
            {value}
          </p>
          <p className="mt-1 text-xs font-black uppercase tracking-widest text-slate-500">
            {label}
          </p>
        </div>

        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          {icon}
        </div>
      </div>
    </div>
  );
}

function AlertBox({
  type,
  message,
  onClose,
}: {
  type: "success" | "error";
  message: string;
  onClose: () => void;
}) {
  const isSuccess = type === "success";

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-sm font-bold ${
        isSuccess
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-rose-200 bg-rose-50 text-rose-700"
      }`}
    >
      <span className="flex items-center gap-2">
        {isSuccess ? (
          <CheckCircle2 className="h-4 w-4" />
        ) : (
          <AlertCircle className="h-4 w-4" />
        )}
        {message}
      </span>

      <button
        type="button"
        onClick={onClose}
        className={`rounded-full p-1 ${
          isSuccess ? "hover:bg-emerald-100" : "hover:bg-rose-100"
        }`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
